import React, { forwardRef, useImperativeHandle, useState } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LinearProgress from '@mui/material/LinearProgress';
import {
    Accordion,
    AccordionDetails,
    Box,
    Card,
    Divider,
    IconButton,
    Typography,
} from '@sunwisesoftware/sunwise-ui';
import PropTypes from 'prop-types';

import StyledAccordionSummary from './StyledAccordionSummary';

const AccordionCard = ({
    cardId,
    children,
    classNameCard,
    classNameExpandIcon = '',
    customTitle = null,
    defaultBehavior = true,
    defaultExpanded = false,
    isHidden = false,
    isLoading = false,
    myForwardedRef = null,
    svgIcon = null,
    sxAccordion,
    sxAccordionSummary,
    sxAccordionDetails,
    sxCard = {},
    sxExpandMoreIcon = {},
    title,
}) => {
    const [expanded, setExpanded] = useState(defaultExpanded);

    const handleChange = (event, isExpanded) => {
        if (defaultBehavior) setExpanded(isExpanded);
        else event.stopPropagation();
    };

    const handleExpandClick = (event) => {
        if (defaultBehavior) return;
        event.stopPropagation();
        setExpanded((prev) => !prev);
    };

    useImperativeHandle(myForwardedRef, () => ({
        hadleClickExpand() {
            setExpanded((prev) => !prev);
        },
        isOpen: open,
    }));

    if (isHidden) return null;

    return (
        <Card className={classNameCard} id={cardId} sx={sxCard}>
            {isLoading && (
                <Box sx={{ width: '100%' }}>
                    <LinearProgress />
                </Box>
            )}
            <Accordion
                defaultExpanded={defaultExpanded}
                expanded={expanded}
                onChange={handleChange}
                sx={sxAccordion}
            >
                <StyledAccordionSummary
                    aria-controls={`${cardId}-content`}
                    expandIcon={
                        defaultBehavior ? (
                            <ExpandMoreIcon
                                className={classNameExpandIcon}
                                sx={sxExpandMoreIcon}
                            />
                        ) : (
                            <IconButton onClick={handleExpandClick}>
                                <ExpandMoreIcon />
                            </IconButton>
                        )
                    }
                    id={`${cardId}-header`}
                    sx={sxAccordionSummary}
                >
                    {svgIcon}
                    {title && (
                        <Typography
                            variant="body2"
                            ml={1}
                            sx={{ fontWeight: 'bold' }}
                        >
                            {title}
                        </Typography>
                    )}
                    {customTitle && <>{customTitle}</>}
                </StyledAccordionSummary>
                <Divider />
                <AccordionDetails sx={sxAccordionDetails}>
                    {children}
                </AccordionDetails>
            </Accordion>
        </Card>
    );
};

AccordionCard.propTypes = {
    cardId: PropTypes.string,
    children: PropTypes.oneOfType([
        PropTypes.array,
        PropTypes.object,
        PropTypes.string,
    ]),
    classNameCard: PropTypes.string,
    classNameExpandIcon: PropTypes.string,
    customTitle: PropTypes.object,
    defaultBehavior: PropTypes.bool,
    defaultExpanded: PropTypes.bool,
    isHidden: PropTypes.bool,
    isLoading: PropTypes.bool,
    myForwardedRef: PropTypes.object,
    svgIcon: PropTypes.object,
    sxAccordion: PropTypes.object,
    sxAccordionSummary: PropTypes.object,
    sxAccordionDetails: PropTypes.object,
    sxCard: PropTypes.object,
    sxExpandMoreIcon: PropTypes.object,
    title: PropTypes.string,
};

const AccordionCardForwared = forwardRef((props, ref) => (
    <AccordionCard {...props} myForwardedRef={ref} />
));

AccordionCardForwared.displayName = 'AccordionCardForwared';

export default AccordionCardForwared;
